import { Injectable } from '@angular/core';
import { Config } from './config';
import { HttpClient, HttpParams } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { NotificationRouteAlarmType, NotificationDetailsLite, NotificationDetailsResponse, NotificationResponseValidation } from '../models/notifications';
import { AlarmTypes, DailyAlarmType, DailyReportTypes } from '../models/alarms';
import { CustomerFeatures, GetCustomAlarmsSummary } from '../models/customer';
import { LocationConfigurations } from '../models/report';

export interface NotificationResponse {
    isError: boolean
}

export interface AlarmType {
    name: string;
    id: AlarmTypes;
    type: NotificationRouteAlarmType;
    featureCondition?: CustomerFeatures;
}

@Injectable()
export class NotificationDashboardService {
    constructor(public http: HttpClient, private translate: TranslateService) {}

    public getAllNotificationDetails(
        cid: number,
        pageSize: number,
        startPage: number,
        searchString: string,
        searchValue: {},
    ) {
        const reqUrl = `${Config.serviceUrl}${Config.urls.notificationDetailsAll}?cid=${cid}&PageSize=${pageSize}&StartPage=${startPage}&searchValue=${searchString}`;
        return this.http.post<NotificationDetailsResponse>(reqUrl, searchValue);
    }
    public getSingleNotificationDetails(cid: number, guid: string): Observable<NotificationDetailsLite> {
        const reqUrl = `${Config.serviceUrl}${Config.urls.notificationDetails}?cid=${cid}&guid=${guid}`;
        return this.http.get<NotificationDetailsLite>(reqUrl);
    }
    public deleteNotification(cid: number, guid: string) {
        const reqUrl = `${Config.serviceUrl}${Config.urls.notificationDetails}?cid=${cid}&guid=${guid}`;
        return this.http.delete<void>(reqUrl);
    }
    public updateNotification(cid: number, body) {
        const reqUrl = `${Config.serviceUrl}${Config.urls.notificationDetails}?cid=${cid}`;
        return this.http.post<NotificationResponse>(reqUrl, body);
    }
    public validateNotification(cid: number, body: NotificationDetailsLite) {
        const reqUrl = `${Config.serviceUrl}${Config.urls.notificationValidate}?cid=${cid}&validateAlarmConfiguration=true`;
        return this.http.post<NotificationResponseValidation>(reqUrl, body);
    }
    public convertAlarmTypeToGeneral() {
        return NotificationRouteAlarmType.Standard;
    }

    public getSpecificDailyAlarmType(id: number): DailyAlarmType | null {
        const allAlarms = this.getAllAlarmTypesDaily();
        return allAlarms.find((x) => x.id === id);
    }
    public getSpecificAlarmType(id: number) {
        const allAlarms = this.getAllAlarmTypes();
        return allAlarms.find((x) => x.id === id);
    }
    public getWDONotConfigured(): string {
        return this.translate.instant('COMMON.WDO_NOT_CONFIGURED');
    }
    public getAllAlarmTypes(): AlarmType[] {
        const alarmTypes: AlarmType[] = [
            {
                name: this.translate.instant('COMMON.LOW_BATTERY_ALARM'),
                id: AlarmTypes.LOW_BATTERY_ALARM,
                type: NotificationRouteAlarmType.Standard,
            },
            {
                name: this.translate.instant('COMMON.HIGH_HIGH_ALARAM'),
                id: AlarmTypes.HIGH_HIGH_ALARM,
                type: NotificationRouteAlarmType.Standard,
            },
            {
                name: this.translate.instant('COMMON.HIGH_FLOW_ALARM'),
                id: AlarmTypes.HIGH_FLOW_ALARM,
                type: NotificationRouteAlarmType.Standard,
            },
            {
                name: this.translate.instant('COMMON.HIGH_LEVEL_ALARM'),
                id: AlarmTypes.HIGH_LEVEL_ALARM,
                type: NotificationRouteAlarmType.Standard,
            },
            {
                name: this.translate.instant('COMMON.LOW_LEVEL_ALARM'),
                id: AlarmTypes.LOW_LEVEL_ALARM,
                type: NotificationRouteAlarmType.Standard,
            },
            { name: this.translate.instant('COMMON.RAIN_ALARM'), id: AlarmTypes.RAIN_ALARM, type: NotificationRouteAlarmType.Standard },
            {
                name: this.translate.instant('COMMON.LOW_FLOW_ALARM'),
                id: AlarmTypes.LOW_FLOW_ALARM,
                type: NotificationRouteAlarmType.Standard,
            },
            {
                name: this.translate.instant('COMMON.SUBMERGED_ALARM'),
                id: AlarmTypes.SUBMERGED_ALARM,
                type: NotificationRouteAlarmType.Standard,
            },
            { name: this.translate.instant('COMMON.TILT_ALARM'), id: AlarmTypes.TILT_ALARM, type: NotificationRouteAlarmType.Standard },
            {
                name: this.translate.instant('COMMON.FULL_PIPE_ALARM'),
                id: AlarmTypes.FULL_PIPE_ALARM,
                type: NotificationRouteAlarmType.Standard,
            },
            {
                name: this.translate.instant('COMMON.OVERFLOW_ALARM'),
                id: AlarmTypes.OVERFLOW_ALARM,
                type: NotificationRouteAlarmType.Standard,
            },
            {
                name: this.translate.instant('COMMON.WET_OVERFLOW_ALARM'),
                id: AlarmTypes.WET_OVERFLOW_ALARM,
                type: NotificationRouteAlarmType.Standard,
                featureCondition: CustomerFeatures.WetDryOverflow,
            },
            {
                name: this.translate.instant('COMMON.DRY_OVERFLOW_ALARM'),
                id: AlarmTypes.DRY_OVERFLOW_ALARM,
                type: NotificationRouteAlarmType.Standard,
                featureCondition: CustomerFeatures.WetDryOverflow,
            },
            { name: 'ANSR', id: AlarmTypes.NONE, type: NotificationRouteAlarmType.ANSR },
        ];

        alarmTypes.sort((a, b) => {
            if (a.name < b.name) {
                return -1;
            }
            if (a.name > b.name) {
                return 1;
            }
            return 0;
        });

        return alarmTypes;
    }

    public getAllAlarmTypesAnsr(): AlarmType[] {
        const ansrAlarmTypes: AlarmType[] = [
            {
                name: this.translate.instant('COMMON.LOW_BATTERY_ALARM'),
                id: AlarmTypes.LOW_BATTERY_ALARM,
                type: NotificationRouteAlarmType.Standard,
            },
            {
                name: this.translate.instant('COMMON.HIGH_HIGH_ALARAM'),
                id: AlarmTypes.HIGH_HIGH_ALARM,
                type: NotificationRouteAlarmType.Standard,
            },
            {
                name: this.translate.instant('COMMON.HIGH_FLOW_ALARM'),
                id: AlarmTypes.HIGH_FLOW_ALARM,
                type: NotificationRouteAlarmType.Standard,
            },
            {
                name: this.translate.instant('COMMON.HIGH_LEVEL_ALARM'),
                id: AlarmTypes.HIGH_LEVEL_ALARM,
                type: NotificationRouteAlarmType.Standard,
            },
            {
                name: this.translate.instant('COMMON.LOW_LEVEL_ALARM'),
                id: AlarmTypes.LOW_LEVEL_ALARM,
                type: NotificationRouteAlarmType.Standard,
            },
            {
                name: this.translate.instant('COMMON.SUBMERGED_ALARM'),
                id: AlarmTypes.SUBMERGED_ALARM,
                type: NotificationRouteAlarmType.Standard,
            },
            { name: this.translate.instant('COMMON.RAIN_ALARM'), id: AlarmTypes.RAIN_ALARM, type: NotificationRouteAlarmType.Standard },
            { name: this.translate.instant('COMMON.TILT_ALARM'), id: AlarmTypes.TILT_ALARM, type: NotificationRouteAlarmType.Standard },
            {
                name: this.translate.instant('COMMON.LOW_FLOW_ALARM'),
                id: AlarmTypes.LOW_FLOW_ALARM,
                type: NotificationRouteAlarmType.Standard,
            },
            {
                name: this.translate.instant('COMMON.FULL_PIPE_ALARM'),
                id: AlarmTypes.FULL_PIPE_ALARM,
                type: NotificationRouteAlarmType.Standard,
            },
            {
                name: this.translate.instant('COMMON.OVERFLOW_ALARM'),
                id: AlarmTypes.OVERFLOW_ALARM,
                type: NotificationRouteAlarmType.Standard,
            },
        ];

        ansrAlarmTypes.sort((a, b) => {
            if (a.name < b.name) {
                return -1;
            }
            if (a.name > b.name) {
                return 1;
            }
            return 0;
        });

        return ansrAlarmTypes;
    }

    public getAllAlarmTypesDaily() {
        const dailyAlarmTypes: DailyAlarmType[] = [
            { name: this.translate.instant('COMMON.COMMUNICATION_ALARM'), id: DailyReportTypes.COMMUNICATION },
            { name: this.translate.instant('COMMON.LOW_BATTERY_ALARM'), id: DailyReportTypes.LOW_BATTERY },
        ];
        return dailyAlarmTypes;
    }

    public getCustomAlarms(cid: number) {
        const httpParams: HttpParams = new HttpParams();
        return this.http.get<GetCustomAlarmsSummary>(Config.serviceUrl + Config.urls.customAlarms + '?customerId=' + cid, {
            params: httpParams,
        });
    }

    public getNotificationConfiguration(cid: number, sort: string, sortdirection: string, pageIndex?: number, pageSize?: number) {
        let url = `${Config.urls.locationNotificationsConfiguration}?cid=${cid}&sort=${sort}&sortdirection=${sortdirection}`;
        if(pageSize) {
            url += `&pageSize=${pageSize}&StartPage=${pageIndex}`;
        }
        return this.http.get<LocationConfigurations>(url);
    }
}
